import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

// A failed dynamic import is almost always a STALE CHUNK: the app was redeployed
// (or the dev server re-optimised deps) while a tab held an old module graph, so
// the hashed chunk URL no longer exists and `import()` rejects with
// "Failed to fetch dynamically imported module". The fix is to fetch the current
// manifest — i.e. reload once. We guard against an infinite reload loop with a
// session flag, and only rethrow (to the route errorElement) if a reload didn't
// help, which means the failure is a genuine error, not a stale chunk.
const RELOAD_FLAG = 'lazy-retry-reloaded';

/**
 * Drop-in replacement for `React.lazy` that self-heals a stale-chunk import
 * failure by triggering a single full-page reload. Only helps FUTURE
 * navigations — a tab that already failed mid-session must be hard-refreshed.
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Clear the guard on any success so a later, unrelated failure can reload.
      window.sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (error) {
      const alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG) === 'true';
      if (!alreadyReloaded) {
        window.sessionStorage.setItem(RELOAD_FLAG, 'true');
        window.location.reload();
        // Reload is taking over; keep Suspense's fallback up with a promise that
        // never settles so React doesn't flash an error before the navigation.
        return new Promise<{ default: T }>(() => {
          /* never resolves — the page reload replaces this document */
        });
      }
      window.sessionStorage.removeItem(RELOAD_FLAG);
      throw error;
    }
  });
}
